/**
 * Purpose: Manages the state for document/file attachments in the chat
 *
 * How it works:
 * 1. When a file is attached:
 *    - Validates file size against limits
 *    - Generates unique identifiers
 *    - Prepares for upload
 * 2. During upload:
 *    - Tracks upload status
 *    - Manages file references
 *    - Handles cleanup
 *
 * Key features:
 * - File size validation
 * - Internationalized error messages
 * - Upload state tracking
 * - Proper cleanup on discard
 */

import { create } from "zustand";
import { generateUuid7 } from "@/utils/uuid7";
import { toast } from "@/hooks/useToast";
import { isUserAuthenticated } from "@/middleware/auth";
import { isArchitectComposerActive } from "@/utils/architectComposer";
import { attachmentSizeRejection } from "@/utils/attachmentSizeLimit";
import { useSubscriptionState } from "./SubscriptionState";

/**
 * State interface for file message attachments
 *
 * @property isUploading - Tracks if a file is currently being uploaded
 * @property newFileAttachment - The actual File object being processed
 * @property newFileAttachmentId - Unique ID for the file attachment
 */
type State = {
  isUploading: boolean;
  newFileAttachment: File | null;
  newFileAttachmentId: string;
};

/**
 * Actions for managing file message attachments
 *
 * @property setIsUploading - Updates upload status
 * @property setNewFileAttachment - Sets the current file
 * @property setNewFileAttachmentId - Sets file identifier
 * @property handleFileAttachment - Main handler for new file attachments
 * @property discardFileAttachment - Cleans up and resets state
 * @property resetState - Resets to initial state
 */
type Actions = {
  setIsUploading: (isUploading: boolean) => void;
  setNewFileAttachment: (file: File | null) => void;
  setNewFileAttachmentId: (id: string) => void;
  handleFileAttachment: (file: File) => Promise<void>;
  discardFileAttachment: () => void;
  resetState: () => void;
};

const initialState: State = {
  isUploading: false,
  newFileAttachment: null,
  newFileAttachmentId: "",
};

export const useFileMessageSenderState = create<State & Actions>()((set) => ({
  // State
  ...initialState,

  // Actions
  setIsUploading: (isUploading) => set({ isUploading }),
  setNewFileAttachment: (file) => set({ newFileAttachment: file }),
  setNewFileAttachmentId: (id) => set({ newFileAttachmentId: id }),

  /**
   * Cleans up resources and resets state when discarding file attachment
   * - Resets all state to initial values
   */
  discardFileAttachment: () => {
    set(initialState);
  },

  /**
   * Handles new file attachments
   *
   * Process:
   * 1. Validates user authentication
   * 2. Validates file size against limits
   * 3. Generates unique identifier
   * 4. Updates state with new file data
   *
   * @param file - The file to process
   */
  handleFileAttachment: async (file: File) => {
    const userFingerPrint = isUserAuthenticated();
    if (!userFingerPrint) {
      return;
    }

    // Resolves the per-plan cap for the destination scope — the Architect
    // ladder in an Architect composer, the regular per-plan ladder elsewhere.
    // Shares its decision with the send-time check in messages.ts so the two
    // cannot disagree.
    const sizeRejection = attachmentSizeRejection(file.size, {
      fileType: file.type,
      isArchitect: isArchitectComposerActive(),
      plan: useSubscriptionState.getState().subscription.plan,
    });
    if (sizeRejection) {
      toast({
        duration: 1500,
        description: sizeRejection,
        variant: "destructive",
      });
      return;
    }

    set({
      newFileAttachmentId: generateUuid7(),
      newFileAttachment: file,
    });
  },

  resetState: () => set(initialState),
}));
